import { notEmpty, parseDate } from "@product/scmp-sdk";
import { useSyncedRef, useUnmountEffect } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { AppBarColorScheme, getAppBarVariant } from "~/components/app-bar/helpers";
import {
  checkCanShowInPlusPlatform,
  checkIsContainsPlusQueryString,
  checkIsPlusArticle,
} from "~/components/article/article-render/plus/helpers";
import { checkIsPostiesArticle } from "~/components/article/article-render/posties/helpers";
import { config } from "~/data";
import { getLoginUrl } from "~/lib/account";
import { useCurrentArticleIds } from "~/lib/current-item";
import {
  closeAndClearUpPianoMeterModal,
  rosettaAtom,
  setPianoCustomVariable,
  setPianoVariable,
} from "~/lib/rosetta";
import type { pianoIntegrationArticle$key } from "~/queries/__generated__/pianoIntegrationArticle.graphql";

import { checkArticleIsInArchiveWall, checkArticleIsInVelocityWall } from "./helpers";

export const usePianoIntegration = (article_: pianoIntegrationArticle$key) => {
  const router = useRouter();
  const { currentArticleId } = useCurrentArticleIds();
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const article = useFragment(
    graphql`
      fragment pianoIntegrationArticle on Article {
        __typename
        contentLock
        createdDate
        entityId
        entityUuid
        headline
        publishedDate
        sponsor
        sponsorType
        urlAlias
        authors {
          name
        }
        sections {
          value {
            entityUuid
            name
          }
        }
        topics {
          name
        }
        types {
          value {
            entityId
            name
          }
        }
        ...helpersAppBarVariantBase
        ...hooksPaywallPromiseContent
        ...helpersIsArchiveWallArticle
        ...helpersIsVelocityWallArticle
        ...helpersCheckIsPostiesArticle
        ...helpersCheckIsPlusArticle
        ...helpersCheckCanShowInPlusPlatformArticle
      }
    `,
    article_,
  );

  const latestRosettaInstance = useSyncedRef(asyncRosettaState?.result?.instance);

  const [isExecuted, setIsExecuted] = useState(false);

  useEffect(() => {
    const appSettings = asyncRosettaState?.result?.appSettings ?? {};
    if (
      !notEmpty(appSettings) ||
      asyncRosettaState?.status !== "success" ||
      currentArticleId !== article.entityId
    )
      return;

    const { oldArticleTimeframe = 5, paywallCampaignName } = appSettings;
    const articleUrl = new URL(location.origin);
    articleUrl.pathname = article.urlAlias;

    const handleSetArticleVariables = () => {
      const subscribeUrl = new URL(config.rosetta.subscribeHost);
      subscribeUrl.searchParams.set("destination", articleUrl.toString());

      setPianoCustomVariable("entityUuid", article.entityUuid);
      setPianoCustomVariable("entityId", article.entityId);
      setPianoCustomVariable("headline", article.headline);
      setPianoCustomVariable("loginUrl", getLoginUrl(articleUrl.toString()));
      setPianoCustomVariable("subscribeUrl", subscribeUrl.toString());
      setPianoCustomVariable("theme", AppBarColorScheme[getAppBarVariant(article)]);
      setPianoCustomVariable("paywallCampaign", paywallCampaignName);
    };

    const handleSetGeneralVariables = () => {
      setPianoVariable("setZone", "Web");
      setPianoVariable("setPageURL", articleUrl.toString());

      const isInArchiveWall = checkArticleIsInArchiveWall(article, oldArticleTimeframe);
      const isInVelocityWall = checkArticleIsInVelocityWall(article);
      const isScmpPlusArticle =
        checkIsPlusArticle(article) ||
        (checkCanShowInPlusPlatform(article) && checkIsContainsPlusQueryString(router));

      const articleType = first(article.types?.flatMap(type => type?.value))?.name ?? "";
      setPianoVariable(
        "setTags",
        [
          // refs: https://project.scmp.tech/product/web/rosetta/-/issues/1084
          ...(article.topics?.map(topic =>
            topic?.name.replaceAll(/["#$%&'()*+.:<>?\\{}~]/g, "_"),
          ) ?? []),
          article.__typename ? `contentType/${article.__typename}` : "",
          article.contentLock ? "" : "contentUnlocked",
          article.sponsorType ? `sponsorType/${article.sponsorType}` : "",
          isInArchiveWall ? "category/archive" : "",
          isInVelocityWall ? "category/velocity" : "",
          notEmpty(articleType) ? `articleType/${articleType}` : "",
          isScmpPlusArticle ? "scmp-plus" : "",
        ].filter(notEmpty),
      );
    };

    const handleSetContentVariables = () => {
      const firstSection = first(article.sections);
      setPianoVariable(
        "setContentCreated",
        parseDate(
          notEmpty(article.publishedDate) ? article.publishedDate : article.createdDate ?? 0,
        ).toISOString(),
      );
      setPianoVariable("setContentAuthor", first(article.authors)?.name);
      setPianoVariable("setContentIsNative", notEmpty(article.sponsor));
      setPianoVariable(
        "setContentSection",
        (firstSection?.value?.map(section => section?.name).slice(-2) ?? []).join("/"),
      );
    };

    handleSetArticleVariables();
    handleSetGeneralVariables();
    handleSetContentVariables();
    if (isExecuted) return;
    closeAndClearUpPianoMeterModal();
    const isPostiesArticle = checkIsPostiesArticle(article);
    if (isPostiesArticle) return;
    latestRosettaInstance.current?.execute();
    setIsExecuted(true);
  }, [
    article,
    asyncRosettaState?.result?.appSettings,
    asyncRosettaState?.status,
    latestRosettaInstance,
    currentArticleId,
    isExecuted,
    router,
  ]);

  useUnmountEffect(() => closeAndClearUpPianoMeterModal());
};
